/**
 * KAN-744: удаление менеджера. Менеджер с клиентами не удаляется: backend
 * отвечает 409 manager_in_use, диалог показывает его текст и предлагает
 * отправить менеджера в архив.
 */
import { useEffect, useState } from 'react';
import { isAxiosError } from 'axios';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useManagerMutations } from '@/hooks/useManagers';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import { MANAGER_IN_USE_CODE, type Manager } from '@/types/manager';

interface Props {
  manager: Manager | null;
  onOpenChange: (open: boolean) => void;
  onDeleted?: () => void;
}

const IN_USE_FALLBACK = 'У менеджера есть клиенты, удалить нельзя. Его можно отправить в архив.';

function inUseDetail(err: unknown): string | null {
  if (!isAxiosError(err) || err.response?.status !== 409) return null;
  const data = err.response.data as { code?: string; detail?: string } | undefined;
  if (data?.code && data.code !== MANAGER_IN_USE_CODE) return null;
  return data?.detail || IN_USE_FALLBACK;
}

export function ManagerDeleteDialog({ manager, onOpenChange, onDeleted }: Props): JSX.Element {
  const { remove, update } = useManagerMutations();
  const [error, setError] = useState<string | null>(null);
  const [inUse, setInUse] = useState(false);

  useEffect(() => {
    setError(null);
    setInUse(false);
  }, [manager]);

  const pending = remove.isPending || update.isPending;

  const doDelete = async (): Promise<void> => {
    if (!manager) return;
    try {
      await remove.mutateAsync(manager.id);
      onOpenChange(false);
      onDeleted?.();
    } catch (err) {
      const detail = inUseDetail(err);
      setInUse(detail !== null);
      setError(detail ?? extractErrorMessage(err, 'Не удалось удалить менеджера'));
    }
  };

  const doArchive = async (): Promise<void> => {
    if (!manager) return;
    try {
      await update.mutateAsync({ id: manager.id, data: { is_active: false } });
      onOpenChange(false);
    } catch (err) {
      setError(extractErrorMessage(err, 'Не удалось отправить в архив'));
    }
  };

  return (
    <Dialog open={!!manager} onOpenChange={onOpenChange}>
      <DialogContent data-testid="manager-delete-dialog">
        <DialogHeader>
          <DialogTitle>Удалить менеджера?</DialogTitle>
          <DialogDescription>{manager?.full_name}</DialogDescription>
        </DialogHeader>
        {error && (
          <p className="text-sm text-red-700" data-testid="manager-delete-error">
            {error}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
            Отмена
          </Button>
          {inUse && manager?.is_active ? (
            <Button data-testid="manager-delete-archive" disabled={pending} onClick={doArchive}>
              В архив
            </Button>
          ) : (
            !inUse && (
              <Button
                variant="destructive"
                data-testid="manager-delete-confirm"
                disabled={pending}
                onClick={doDelete}
              >
                Удалить
              </Button>
            )
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
